import { useMemo, useState } from "react";
import { Area, AreaChart, Bar, BarChart, CartesianGrid, Cell, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Eye, Heart, MessageCircle, Repeat2, Percent, Clapperboard, TrendingUp, TrendingDown, ExternalLink, Search, ArrowUpDown, X, Trophy, MessagesSquare } from "lucide-react";
import { PF, n, pct, dd } from "@/lib/reports";
import { lastName } from "@/lib/constants";

const DAYS = ["Lun", "Mar", "Mie", "Joi", "Vin", "Sâm", "Dum"];
const BUCKETS = [[0, 1e3, "<1k"], [1e3, 5e3, "1–5k"], [5e3, 1e4, "5–10k"], [1e4, 5e4, "10–50k"], [5e4, 1e5, "50–100k"], [1e5, Infinity, "100k+"]];
const card = "surface-card rounded-3xl border border-zinc-800/70 bg-[#12141C] p-5 sm:p-6";
const er = (t) => (t.views ? ((t.likes + t.comments + (t.shares || 0)) / t.views) * 100 : null);
const sum = (ps) => ps.reduce((a, p) => ({ views: a.views + (p.views || 0), likes: a.likes + (p.likes || 0), comments: a.comments + (p.comments || 0), shares: a.shares + (p.shares || 0), posts: a.posts + 1 }),
  { views: 0, likes: 0, comments: 0, shares: 0, posts: 0 });

const Delta = ({ cur, prev }) => {
  const c = pct(cur, prev);
  if (c == null) return null;
  return <span className={`inline-flex items-center gap-0.5 text-xs font-semibold ${c >= 0 ? "text-emerald-500" : "text-rose-500"}`}>{c >= 0 ? <TrendingUp className="h-3.5 w-3.5" /> : <TrendingDown className="h-3.5 w-3.5" />}{c >= 0 ? "+" : ""}{c}%</span>;
};

export const Kpis = ({ t, prev, testId = "report-kpis" }) => {
  const items = [[Eye, "Vizualizări", t.views, prev?.views], [Heart, "Aprecieri", t.likes, prev?.likes], [MessageCircle, "Comentarii", t.comments, prev?.comments],
    [Repeat2, "Distribuiri", t.shares, prev?.shares], [Percent, "Interacțiune", er(t), prev && er(prev)], [Clapperboard, "Clipuri", t.posts, prev?.posts]];
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2.5" data-testid={testId}>
      {items.map(([Icon, label, v, p]) => (
        <div key={label} className="rounded-2xl border border-zinc-800/70 bg-zinc-800/30 px-3.5 py-3" data-testid={`${testId}-${label}`}>
          <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wide text-zinc-500"><Icon className="h-3.5 w-3.5" />{label}</div>
          <div className="mt-1 font-display text-2xl font-bold text-zinc-100">{label === "Interacțiune" ? (v == null ? "—" : `${v.toFixed(1)}%`) : n(v)}</div>
          {p ? <Delta cur={v} prev={p} /> : <span className="text-xs text-zinc-600">—</span>}
        </div>
      ))}
    </div>
  );
};

const Box = ({ title, children, testId, className = "" }) => (
  <section className={`${card} ${className}`} data-testid={testId}>
    <h3 className="mb-3 text-xs font-semibold uppercase tracking-[0.14em] text-zinc-500">{title}</h3>{children}
  </section>
);
const tip = { contentStyle: { background: "#12141C", border: "1px solid #27272a", borderRadius: 12, fontSize: 12 }, formatter: (v) => n(v) };

const ViewsOverTime = ({ posts }) => {
  const data = useMemo(() => {
    const m = {};
    posts.forEach((p) => { const d = (p.posted_at || "").slice(0, 10); if (d) m[d] = (m[d] || 0) + (p.views || 0); });
    return Object.keys(m).sort().map((d) => ({ d: dd(d), v: m[d] }));
  }, [posts]);
  return (
    <ResponsiveContainer width="100%" height={200}>
      <AreaChart data={data}><defs><linearGradient id="gv" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#F59E0B" stopOpacity={0.5} /><stop offset="100%" stopColor="#F59E0B" stopOpacity={0} /></linearGradient></defs>
        <CartesianGrid strokeDasharray="3 3" stroke="#27272a" vertical={false} /><XAxis dataKey="d" tick={{ fontSize: 11, fill: "#71717a" }} /><YAxis tickFormatter={n} tick={{ fontSize: 11, fill: "#71717a" }} width={42} />
        <Tooltip {...tip} /><Area type="monotone" dataKey="v" name="Vizualizări" stroke="#F59E0B" strokeWidth={2.5} fill="url(#gv)" animationDuration={700} /></AreaChart>
    </ResponsiveContainer>
  );
};

const Platforms = ({ posts }) => {
  const data = Object.keys(PF).map((k) => ({ k, name: PF[k].label, ...sum(posts.filter((p) => p.platform === k)) })).filter((x) => x.posts);
  return (
    <ResponsiveContainer width="100%" height={200}>
      <BarChart data={data}><CartesianGrid strokeDasharray="3 3" stroke="#27272a" vertical={false} /><XAxis dataKey="name" tick={{ fontSize: 11, fill: "#71717a" }} /><YAxis tickFormatter={n} tick={{ fontSize: 11, fill: "#71717a" }} width={42} />
        <Tooltip {...tip} /><Bar dataKey="views" name="Vizualizări" radius={[8, 8, 0, 0]} animationDuration={700}>{data.map((x) => <Cell key={x.k} fill={x.k === "tiktok" ? "#25F4EE" : PF[x.k].color} />)}</Bar></BarChart>
    </ResponsiveContainer>
  );
};

const Heatmap = ({ posts }) => {
  const grid = useMemo(() => {
    const g = Array.from({ length: 7 }, () => Array.from({ length: 6 }, () => ({ v: 0, c: 0 })));
    posts.forEach((p) => { if (!p.posted_at) return; const d = new Date(p.posted_at); const cell = g[(d.getDay() + 6) % 7][Math.floor(d.getHours() / 4)]; cell.v += p.views || 0; cell.c += 1; });
    return g;
  }, [posts]);
  const max = Math.max(1, ...grid.flat().map((x) => (x.c ? x.v / x.c : 0)));
  return (
    <div className="overflow-x-auto" data-testid="report-heatmap">
      <div className="grid min-w-[300px] grid-cols-[2.5rem_repeat(6,1fr)] gap-1 text-[10px] text-zinc-500">
        <span />{["0–4", "4–8", "8–12", "12–16", "16–20", "20–24"].map((h) => <span key={h} className="text-center">{h}</span>)}
        {grid.map((row, i) => [<span key={`l${DAYS[i]}`} className="self-center">{DAYS[i]}</span>, ...row.map((x, j) => {
          const a = x.c ? x.v / x.c / max : 0;
          return <span key={`${DAYS[i]}-${j}`} title={x.c ? `${x.c} clipuri · medie ${n(Math.round(x.v / x.c))}` : "fără clipuri"} className="h-7 rounded-md border border-zinc-800/60" style={{ background: a ? `rgba(245,158,11,${0.15 + a * 0.85})` : "transparent" }} />;
        })])}
      </div>
      <p className="mt-2 text-[11px] text-zinc-500">Media vizualizărilor după ziua și ora postării. Mai închis = merge mai bine.</p>
    </div>
  );
};

const Distribution = ({ posts }) => {
  const data = BUCKETS.map(([lo, hi, l]) => ({ l, c: posts.filter((p) => (p.views || 0) >= lo && (p.views || 0) < hi).length }));
  return (
    <ResponsiveContainer width="100%" height={200}>
      <BarChart data={data}><XAxis dataKey="l" tick={{ fontSize: 11, fill: "#71717a" }} /><YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#71717a" }} width={28} />
        <Tooltip contentStyle={tip.contentStyle} /><Bar dataKey="c" name="Clipuri" fill="#8B5CF6" radius={[8, 8, 0, 0]} animationDuration={700} /></BarChart>
    </ResponsiveContainer>
  );
};

const Thumb = ({ p, thumbOf, className }) => {
  const src = thumbOf(p);
  return src ? <img src={src} alt="" loading="lazy" referrerPolicy="no-referrer" className={`${className} object-cover bg-zinc-800`} onError={(e) => { e.currentTarget.style.visibility = "hidden"; }} />
    : <span className={className} style={{ background: PF[p.platform].color }} />;
};

export const TopClips = ({ posts, thumbOf, testId = "report-top" }) => {
  const top = [...posts].sort((a, b) => (b.views || 0) - (a.views || 0)).slice(0, 5);
  if (!top.length) return null;
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3" data-testid={testId}>
      {top.map((p, i) => (
        <a key={p.key} href={p.url} target="_blank" rel="noopener noreferrer" data-testid={`${testId}-${i}`} className="group relative overflow-hidden rounded-2xl border border-zinc-800/70 bg-zinc-900 transition-transform hover:-translate-y-0.5">
          <Thumb p={p} thumbOf={thumbOf} className="block aspect-[3/4] w-full" />
          <span className="absolute left-2 top-2 grid h-7 w-7 place-items-center rounded-full bg-amber-500 text-sm font-bold text-[#0B0D13]">{i + 1}</span>
          <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/90 to-transparent p-2.5 pt-8 text-white">
            <div className="font-display text-xl font-bold">{n(p.views)}</div>
            <div className="truncate text-[11px] opacity-80">{p.client_name ? `${lastName(p.client_name)} · ` : ""}{PF[p.platform].label} · {p.posted_at ? dd(p.posted_at.slice(0, 10)) : ""}</div>
          </div>
        </a>
      ))}
    </div>
  );
};

const COLS = [["posted_at", "Data"], ["views", "Vizualizări"], ["likes", "Aprecieri"], ["comments", "Coment."], ["shares", "Distrib."]];

const PostsTable = ({ posts, thumbOf, onUnmark }) => {
  const [sort, setSort] = useState(["views", -1]);
  const [qq, setQq] = useState("");
  const rows = posts.filter((p) => !qq || `${p.caption} ${p.clip_title} ${p.client_name}`.toLowerCase().includes(qq.toLowerCase()))
    .sort((a, b) => ((a[sort[0]] || 0) > (b[sort[0]] || 0) ? 1 : -1) * sort[1]);
  return (
    <div data-testid="report-table">
      <label className="mb-3 flex items-center gap-2 rounded-xl border border-zinc-800 bg-[#0B0D13] px-3"><Search className="h-4 w-4 text-zinc-500" />
        <input value={qq} onChange={(e) => setQq(e.target.value)} placeholder="Caută în clipuri…" data-testid="report-table-search" className="min-h-[42px] flex-1 bg-transparent text-sm outline-none" /></label>
      <div className="mb-2 flex gap-1.5 overflow-x-auto no-scrollbar">
        {COLS.map(([k, l]) => <button key={k} type="button" onClick={() => setSort([k, sort[0] === k ? -sort[1] : -1])} data-testid={`report-sort-${k}`}
          className={`flex shrink-0 items-center gap-1 rounded-full border px-3 py-1.5 text-xs ${sort[0] === k ? "border-amber-500 text-amber-500" : "border-zinc-800 text-zinc-400"}`}><ArrowUpDown className="h-3 w-3" />{l}</button>)}
      </div>
      <div className="divide-y divide-zinc-800/70">
        {rows.map((p) => (
          <div key={p.key} className="flex items-center gap-3 py-2.5" data-testid={`report-row-${p.key}`}>
            <Thumb p={p} thumbOf={thumbOf} className="h-14 w-11 shrink-0 rounded-lg" />
            <a href={p.url} target="_blank" rel="noopener noreferrer" className="min-w-0 flex-1 hover:text-amber-500">
              <div className="truncate text-sm font-medium">{p.caption || "Fără descriere"}</div>
              <div className="mt-0.5 flex flex-wrap gap-x-3 text-xs text-zinc-500">
                <b style={{ color: PF[p.platform].color }}>{PF[p.platform].short}</b>{p.client_name && <span>{lastName(p.client_name)}</span>}{p.posted_at && <span>{dd(p.posted_at.slice(0, 10))}</span>}
                <span className="sm:hidden">👁 {n(p.views)} · ❤️ {n(p.likes)}</span>
              </div>
            </a>
            <div className="hidden sm:grid grid-cols-4 gap-4 text-right font-mono text-sm">{["views", "likes", "comments", "shares"].map((k) => <span key={k} className="w-16">{n(p[k])}</span>)}</div>
            <ExternalLink className="hidden h-4 w-4 shrink-0 text-zinc-600 sm:block" />
            {onUnmark && <button type="button" onClick={() => onUnmark(p)} title="Nu e al nostru" data-testid={`report-unmark-${p.key}`} className="rounded-lg p-1.5 text-zinc-500 hover:text-rose-500"><X className="h-4 w-4" /></button>}
          </div>
        ))}
        {!rows.length && <p className="py-6 text-center text-sm text-zinc-500">Niciun clip.</p>}
      </div>
    </div>
  );
};

const List = ({ title, items, tone }) => (items?.length ? (
  <div><h4 className="mb-2 text-sm font-semibold">{title}</h4>
    <div className="space-y-2">{items.map((t) => (
      <div key={t.name} className="rounded-xl bg-zinc-800/30 px-3 py-2">
        <div className="flex items-center gap-2 text-sm"><span className="font-medium">{t.name}</span>
          {t.trend && t.trend !== "stabil" && <span className="rounded-full bg-sky-500/15 px-2 text-[10px] text-sky-400">{t.trend}</span>}
          <span className={`ml-auto font-mono text-xs ${tone === "neg" ? "text-rose-400" : "text-amber-500"}`}>{t.pct}%</span></div>
        <div className="mt-1 h-1 rounded-full bg-zinc-800"><div className={`h-1 rounded-full ${tone === "neg" ? "bg-rose-500" : "bg-amber-500"}`} style={{ width: `${Math.min(100, t.pct || 0)}%` }} /></div>
        {t.examples?.slice(0, 2).map((e) => <div key={e} className="mt-1 text-xs italic text-zinc-500">„{e}”</div>)}
      </div>
    ))}</div></div>) : null);

export const Insights = ({ ins, testId }) => (
  <Box title={`Ce spun oamenii · ${ins.count || 0} comentarii`} testId={testId}>
    {ins.summary && <p className="mb-3 text-sm text-zinc-300">{ins.summary}</p>}
    {ins.tone && (
      <div className="mb-4"><div className="flex h-3 overflow-hidden rounded-full" data-testid={`${testId}-tone`}>
        <span className="bg-emerald-500" style={{ width: `${ins.tone.pos}%` }} /><span className="bg-zinc-500" style={{ width: `${ins.tone.neu}%` }} /><span className="bg-rose-500" style={{ width: `${ins.tone.neg}%` }} /></div>
        <div className="mt-1 flex gap-4 text-xs text-zinc-500"><span>😊 {ins.tone.pos}% pozitiv</span><span>😐 {ins.tone.neu}% neutru</span><span>😠 {ins.tone.neg}% negativ</span></div></div>
    )}
    <div className="grid gap-4 md:grid-cols-2"><List title="Teme" items={ins.themes} /><List title="Probleme și nemulțumiri" items={ins.problems} tone="neg" />
      <List title="Întrebări frecvente" items={ins.questions} /><List title="Ce le place" items={ins.praise} /></div>
  </Box>
);

const Leaderboard = ({ clients }) => {
  const max = Math.max(1, ...clients.map((c) => c.t.views));
  return (
    <ol className="space-y-2.5" data-testid="report-leaderboard">
      {clients.map((c, i) => (
        <li key={c.client.id} className="grid grid-cols-[1.75rem_1fr_auto] items-center gap-3" data-testid={`leader-row-${c.client.id}`}>
          <span className={`font-display text-lg font-bold ${i < 3 ? "text-amber-500" : "text-zinc-500"}`}>{i + 1}</span>
          <div className="min-w-0"><div className="flex items-center gap-2 text-sm"><span className="truncate font-semibold">{lastName(c.client.name)}</span><Delta cur={c.t.views} prev={c.prev.views} /></div>
            <div className="mt-1 h-2 rounded-full bg-zinc-800"><div className="h-2 rounded-full transition-[width] duration-700" style={{ width: `${(c.t.views / max) * 100}%`, background: c.client.color || "#F59E0B" }} /></div></div>
          <span className="font-mono text-sm">{n(c.t.views)}</span>
        </li>
      ))}
    </ol>
  );
};

const ClientSection = ({ c, photo, thumbOf, onUnmark }) => (
  <section className={`${card} space-y-5`} data-testid={`report-client-${c.client.id}`}>
    <div className="flex items-center gap-4">
      {photo ? <img src={photo} alt="" className="h-16 w-16 rounded-2xl object-cover" /> : <span className="h-16 w-16 rounded-2xl" style={{ background: c.client.color }} />}
      <div className="min-w-0"><h3 className="truncate font-display text-2xl font-bold tracking-tight">{lastName(c.client.name)}</h3>
        <div className="mt-1 flex flex-wrap gap-2">{Object.entries(c.growth || {}).map(([p, g]) => (
          <a key={p} href={g.url} target="_blank" rel="noopener noreferrer" className="rounded-full border border-zinc-700 px-2.5 py-0.5 text-xs text-zinc-300"><b style={{ color: PF[p].color }}>{PF[p].short}</b> {n(g.followers)} urmăritori{g.new ? ` (${g.new > 0 ? "+" : ""}${n(g.new)})` : ""}</a>))}</div></div>
    </div>
    <Kpis t={c.t} prev={c.prev} testId={`report-kpis-${c.client.id}`} />
    {c.posts.length > 1 && <ViewsOverTime posts={c.posts} />}
    <TopClips posts={c.posts} thumbOf={thumbOf} testId={`report-top-${c.client.id}`} />
    {c.insights && <Insights ins={c.insights} testId={`report-insights-${c.client.id}`} />}
    {!!c.posts.length && <details><summary className="cursor-pointer text-sm text-zinc-400" data-testid={`report-all-${c.client.id}`}>Toate clipurile ({c.posts.length})</summary><div className="mt-3"><PostsTable posts={c.posts} thumbOf={thumbOf} onUnmark={onUnmark} /></div></details>}
    {!c.posts.length && <p className="text-sm text-zinc-500">Niciun clip în perioada asta.</p>}
  </section>
);

export const ReportBody = ({ rep, photoOf, thumbOf = (p) => p.thumb, platform = "", onUnmark }) => {
  const clients = useMemo(() => rep.clients.map((r) => {
    const posts = r.posts.filter((p) => !platform || p.platform === platform).map((p) => ({ ...p, client_name: r.client.name }));
    return { ...r, posts, t: sum(posts), prev: platform ? {} : { ...r.prev, posts: r.prev.posts || 0 } };
  }).sort((a, b) => b.t.views - a.t.views), [rep, platform]);
  const all = clients.flatMap((c) => c.posts);
  const t = sum(all);
  const prev = clients.reduce((a, c) => ({ views: a.views + (c.prev.views || 0), likes: a.likes + (c.prev.likes || 0), comments: a.comments + (c.prev.comments || 0), shares: a.shares + (c.prev.shares || 0), posts: a.posts + (c.prev.posts || 0) }),
    { views: 0, likes: 0, comments: 0, shares: 0, posts: 0 });
  if (!rep.clients.length) return <p className="text-zinc-500" data-testid="report-empty">Niciun client cu conturi legate. Adaugă conturile în tabul „Conturi”.</p>;
  return (
    <div className="space-y-5" data-testid="report-body">
      <section className={card}><Kpis t={t} prev={platform ? null : prev} /></section>
      <div className="grid gap-5 lg:grid-cols-2">
        <Box title="Evoluția vizualizărilor" testId="report-chart-time"><ViewsOverTime posts={all} /></Box>
        <Box title="Pe platforme" testId="report-chart-platforms"><Platforms posts={all} /></Box>
        <Box title="Cea mai bună zi și oră" testId="report-chart-heatmap"><Heatmap posts={all} /></Box>
        <Box title="Distribuția clipurilor după vizualizări" testId="report-chart-dist"><Distribution posts={all} /></Box>
      </div>
      {clients.length > 1 && <Box title={<span className="flex items-center gap-1.5"><Trophy className="h-4 w-4 text-amber-500" />Clasamentul clienților</span>} testId="report-rank"><Leaderboard clients={clients} /></Box>}
      <Box title="Top 5 clipuri" testId="report-top-box"><TopClips posts={all} thumbOf={thumbOf} /></Box>
      <Box title={`Toate clipurile (${all.length})`} testId="report-table-box"><PostsTable posts={all} thumbOf={thumbOf} onUnmark={onUnmark} /></Box>
      {clients.map((c) => <ClientSection key={c.client.id} c={c} photo={photoOf?.(c)} thumbOf={thumbOf} onUnmark={onUnmark} />)}
      <p className="flex items-center gap-1.5 text-xs text-zinc-500"><MessagesSquare className="h-3.5 w-3.5" />{rep.source === "all" ? "Toate postările clienților" : "Doar clipurile făcute de echipă"}{rep.last_sync ? ` · ultima actualizare ${new Date(rep.last_sync).toLocaleString("ro-RO")}` : ""}.</p>
    </div>
  );
};
